import { makeStyles, tokens } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";

const REMOVE_BUTTON_WIDTH = "32px";

export const useEditObjectiveStyles = makeStyles({
  dialogSurface: {
    maxWidth: "600px",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },
  contentFrame: {
    position: "relative",
  },
  contentWrapper: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
    maxHeight: "60vh",
    overflowY: "auto" as const,
  },
  dialogTitleContent: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
  dialogTitleInfoIcon: {
    cursor: "pointer",
  },
  requiredQuestionsField: {
    marginTop: designTokens.spacing[3],
    marginBottom: designTokens.spacing[1],
    fontWeight: designTokens.fontWeight.semibold,
  },
  sampleQuestionRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    gap: designTokens.spacing[2],
    alignItems: "center",
    marginBottom: designTokens.spacing[1],
  },
  multipleChoiceCard: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    padding: designTokens.spacing[3],
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.md,
    marginBottom: designTokens.spacing[2],
  },
  multipleChoiceQuestionRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto auto auto",
    gap: "0px",
    alignItems: "center",
  },
  multipleChoiceOptionsSection: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    paddingLeft: designTokens.spacing[4],
  },
  multipleChoiceAddAnswerRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: designTokens.spacing[1],
  },
  multipleChoiceOptionRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: designTokens.spacing[1],
    alignItems: "center",
  },
  reorderableRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto auto",
    gap: designTokens.spacing[2],
    alignItems: "center",
    marginBottom: designTokens.spacing[1],
  },
  scaleLabelRow: {
    display: "grid",
    gridTemplateColumns: "24px 1fr 32px 32px",
    gap: designTokens.spacing[2],
    alignItems: "center",
    marginBottom: designTokens.spacing[1],
  },
  scaleLabelNumber: {
    textAlign: "center",
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[700],
  },
  addAnswerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    boxSizing: "border-box",
    gap: designTokens.spacing[4],
    flexWrap: "wrap",
    paddingRight: `${designTokens.spacing[2]}`,
  },
  allowMultipleSelectionCheckbox: {
    "& .fui-Checkbox__label": {
      color: designTokens.neutral[900],
      marginLeft: "-7px"
    },
    ":hover .fui-Checkbox__label": {
      color: designTokens.neutral[900],
    },
    ":active .fui-Checkbox__label": {
      color: designTokens.neutral[900],
    },
  },
  allowMultipleSelectionLabel: {
    paddingLeft: '0px',
    paddingRight: '0px',
  },
  addQuestionButton: {
    width: "100%",
    marginTop: designTokens.spacing[1],
    flexShrink: 0,
  },
  addAnswerButton: {
    width: "fit-content",
    marginTop: designTokens.spacing[1],
    flexShrink: 0,
  },
  questionSectionWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
  },
  statementsWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  generateTab: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
    paddingBottom: designTokens.spacing[4],
    maxHeight: "calc(60vh + 100px)",
    overflowY: "auto" as const,
  },
  generateOutput: {
    padding: designTokens.spacing[4],
    paddingLeft: designTokens.spacing[5],
    border: `1px solid ${designTokens.neutral[200]}`,
    borderTopLeftRadius: designTokens.borderRadius.md,
    borderTopRightRadius: designTokens.borderRadius.md,
    borderBottomLeftRadius: "0",
    borderBottomRightRadius: "0",
    borderBottom: "none",
    background: designTokens.neutral[50],
    minHeight: "168px",
    overflowY: "auto" as const,
    whiteSpace: "pre-wrap" as const,
  },
  textareaWithFooter: {
    display: "flex",
    flexDirection: "column" as const,
  },
  textareaNoBottomRadius: {
    borderBottomLeftRadius: "0",
    borderBottomRightRadius: "0",
    borderBottom: "none",
  },
  generateOutputPlaceholder: {
    color: designTokens.neutral[400],
    fontStyle: "italic",
  },
  inputFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[2]}`,
    border: `1px solid ${designTokens.neutral[200]}`,
    borderTop: "none",
    borderBottomLeftRadius: designTokens.borderRadius.md,
    borderBottomRightRadius: designTokens.borderRadius.md,
    background: designTokens.neutral[50],
  },
  typeSelectorField: {
    display: "flex",
    flexDirection: "column",
  },
  radioTooltipTrigger: {
    display: "inline-flex",
    width: "fit-content",
    alignSelf: "flex-start",
    "&:not(:first-child)": {
      marginTop: "-14px",
    },
    "& .fui-Radio__label": {
      paddingLeft: designTokens.spacing[1],
    },
  },
  visuallyHidden: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
  questionsNote: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
  },
  generateObjectiveLink: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    cursor: "pointer",
    textDecoration: "underline",
    marginBottom: designTokens.spacing[2],
  },
});
